import DateFormat from '@/Enums/Format/DateFormat.enum';
import PixelStatus from '@/Enums/Pixels/PixelStatus.enum';
import type { ISegmentControlOption } from '@/Interfaces/ControlOption.interface';
import type { IState } from '@/Interfaces/Pixels/State.interface';
import { DEFAULT_FILTERS, DEFAULT_PIXEL, DEFAULT_PIXELS_FIRED_BY_DAY } from '@/constants/pixels';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import { fulfilledReportCaseHandler } from '@/helpers/beeswaxReports';
import { mergeEvents } from '@/helpers/pixels';
import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '../asyncThunkHandlers';
import { getPixelTag, getPixels, getPixelsReport, getSegments, updateActiveStatus } from './pixelsThunks';

const initialState: IState = {
  filters: DEFAULT_FILTERS,

  pixels: [],
  pixelsRequestId: null,

  pixelsReport: [],
  pixelsReportRequestId: null,

  selectedPixel: DEFAULT_PIXEL,
  selectedPixelTag: null,
  selectedPixelTagRequestId: DEFAULT_LOADING_REQUEST_ID,

  segments: [],
  segmentsRequestId: DEFAULT_LOADING_REQUEST_ID,
  activeStatusRequestId: null,
};

export const pixelsSlice = createSlice({
  name: 'pixels',
  initialState,
  reducers: {
    setSelectedPixel: (state, action) => {
      state.selectedPixel = action.payload;
    },
    resetSelectedPixel: state => {
      state.selectedPixel = DEFAULT_PIXEL;
      state.selectedPixelTag = null;
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters: state => {
      state.filters = DEFAULT_FILTERS;
    },
    addNewPixel: (state, action) => {
      const { id, eventId, name, advertiser, segment, clickWindow, viewWindow, active } = action.payload;
      state.pixels = [
        ...state.pixels,
        {
          id,
          eventId,
          name,
          advertiser,
          segment,
          status: PixelStatus.NotFiring,
          firedToday: 0,
          firedLast7Days: 0,
          pixelsFiredByDay: [...DEFAULT_PIXELS_FIRED_BY_DAY],
          action: id,
          clickWindow,
          viewWindow,
          dateCreated: moment().format(DateFormat.Iso8601),
          active: active ?? true,
        },
      ];
    },
    addNewSegment: (state, action: PayloadAction<ISegmentControlOption>) => {
      state.segments = [...state.segments, action.payload];
    },
    updatePixel: (state, action) => {
      const { id, viewWindow, clickWindow, name } = action.payload;

      state.pixels = state.pixels.map(pixel => {
        if (pixel.id !== id) return pixel;
        return { ...pixel, viewWindow, clickWindow, name };
      });
    },
    mergeEventData: state => {
      state.pixels = mergeEvents(state.pixels, state.pixelsReport);
    },
    resetPixelsPageState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(getPixels.pending, (state, action) => {
        state.pixelsRequestId = action.meta.requestId;
        state.pixels = [];
      })
      .addCase(getPixels.rejected, rejectedCaseHandler('pixels'))
      .addCase(getPixels.fulfilled, (state, action) => {
        if (state.pixelsRequestId === action.meta.requestId) {
          state.pixels = action.payload;
          state.pixelsRequestId = null;
        }
      })
      .addCase(getPixelsReport.pending, pendingCaseHandler('pixelsReport', []))
      .addCase(getPixelsReport.rejected, rejectedCaseHandler('pixelsReport'))
      .addCase(getPixelsReport.fulfilled, fulfilledReportCaseHandler('pixelsReport'))
      .addCase(getSegments.pending, pendingCaseHandler('segments'))
      .addCase(getSegments.rejected, rejectedCaseHandler('segments'))
      .addCase(getSegments.fulfilled, fulfilledCaseHandler('segments'))
      .addCase(getPixelTag.pending, pendingCaseHandler('selectedPixelTag'))
      .addCase(getPixelTag.rejected, rejectedCaseHandler('selectedPixelTag'))
      .addCase(getPixelTag.fulfilled, fulfilledCaseHandler('selectedPixelTag'))
      .addCase(updateActiveStatus.pending, pendingCaseHandler('activeStatus'))
      .addCase(updateActiveStatus.rejected, rejectedCaseHandler('activeStatus'))
      .addCase(updateActiveStatus.fulfilled, (state, action) => {
        if (state.activeStatusRequestId === action.meta.requestId) {
          state.activeStatusRequestId = null;
          state.pixels = state.pixels.filter(pixel => action.payload.id !== pixel.id);
        }
      });
  },
});

export const {
  setFilters,
  resetFilters,
  setSelectedPixel,
  resetSelectedPixel,
  addNewPixel,
  updatePixel,
  addNewSegment,
  mergeEventData,
  resetPixelsPageState,
} = pixelsSlice.actions;

export default pixelsSlice.reducer;
